/**
 * _intent_watch.js — the device's offline edits waiting to sync, watched once
 * for every controller that shows them in place: offline_slot_controller.js
 * (a field's pending value) and offline_tap_controller.js (a time tap's
 * pending state).
 *
 * It reads only powersync_write_requests, the rows offline_intent_controller.js
 * queues, and the connector's outcome for each ('powersync:intent-result',
 * static/js/powersync/connector.js). Never synced rows.
 *
 * State is module-level so one watch serves every element, and it outlives
 * Turbo visits: an element on the next page, or one re-rendered by a Turbo
 * Stream, subscribes again and finds the same pending edits.
 */

// `waiting`: still in PowerSync's upload queue. An uploaded row stays in the
// table until the service's next checkpoint removes it, so presence alone
// would call an applied edit pending on a page opened in between.
const INTENTS_SQL = "SELECT r.id, r.operation, r.target_id, r.record_version, r.payload, r.requested_at, "
    + "EXISTS (SELECT 1 FROM ps_crud c WHERE json_extract(c.data, '$.id') = r.id) AS waiting "
    + "FROM powersync_write_requests r ORDER BY r.requested_at";

/** The words shown beside a value or a card in each sync state. */
export const STATUS_TEXT = { pending: "Waiting to sync", synced: "Synced", derived: "updates after sync" };

/** Intent row id -> { operation, target, record, base, fields, requestedAt }, kept after upload. */
export const intents = new Map();
/** Ids of the intent rows still on the device. */
export const pendingIds = new Set();
/** Intent row id -> the connector's { applied, recordVersion, message }, with
 *  `at`, the device time (ms) it arrived. */
export const outcomes = new Map();

const listeners = new Set();
let watching = false;

/** "job:12" from "job:12:2026-...". */
export function recordOf(version) {
    return (version || "").split(":").slice(0, 2).join(":");
}

function readIntent(row) {
    let pairs = [];
    try {
        pairs = JSON.parse(row.payload || "[]");
    } catch (_e) { /* an unreadable payload fills no slot */ }
    return {
        operation: row.operation || "",
        target: row.target_id || "",
        record: recordOf(row.record_version),
        base: row.record_version || "",
        fields: new Map(Array.isArray(pairs) ? pairs : []),
        requestedAt: row.requested_at || "",
    };
}

function notify() {
    listeners.forEach((listener) => listener());
}

function startWatching() {
    const db = window.chippiePowerSync;
    if (watching || !db) return;
    watching = true;
    db.watch(INTENTS_SQL, [], {
        onResult: (results) => {
            const rows = results?.rows?._array || [];
            rows.forEach((row) => intents.set(row.id, readIntent(row)));
            pendingIds.clear();
            rows.filter((row) => row.waiting).forEach((row) => pendingIds.add(row.id));
            notify();
        },
        onError: (error) => console.error("[intent-watch] could not watch pending edits", error),
    });
}

document.addEventListener("powersync:ready", startWatching);
document.addEventListener("powersync:intent-result", (event) => {
    outcomes.set(event.detail.id, { ...event.detail, at: Date.now() });
    pendingIds.delete(event.detail.id);
    notify();
});

/** Call ``listener`` whenever the pending edits change; starts the watch. */
export function subscribe(listener) {
    listeners.add(listener);
    startWatching();
}

export function unsubscribe(listener) {
    listeners.delete(listener);
}
